import React, { useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Sparkles, Send, Square, Trash2 } from 'lucide-react';
import { useBrand } from '../../context/BrandContext';
import { gameDepartmentAPI } from '../../services/api';
import AskScopeBar, { EMPTY_SCOPE, isScopeEmpty, describeScope } from './AskScopeBar';
import { linkify } from './linkify';

const EMPTY_OPTIONS = { producers: [], providers: [] };

const TOOL_STATUS = {
  read_knowledge: 'Checking the knowledge base…',
  list_dimension_values: 'Looking up catalog values…',
  find_producers_providers: 'Resolving producer/provider names…',
  search_games: 'Searching games…',
  ask_clarifying_question: 'Preparing a follow-up question…',
};

// Request shape for the backend's AskScope: only ids for games (title and
// provider ride along for the prompt), null when nothing is picked.
function toRequestScope(scope) {
  if (isScopeEmpty(scope)) return null;
  return {
    games: scope.games.map(({ unique_game_id, game_title, provider }) => ({ unique_game_id, game_title, provider })),
    producers: scope.producers,
    providers: scope.providers,
    from: scope.from,
    to: scope.to,
  };
}

const SUGGESTIONS = [
  'Which games had the highest GGR in the last 30 days?',
  'How does Pragmatic Play compare to Evolution this month?',
  'Is Plinko - Originals trending up or down over the last 90 days?',
];

// Parses the SSE stream from POST /api/game-department/ask: `data: {"text": "..."}`
// chunks to append, `data: {"status": "tool_start"|"tool_done", ...}` progress
// hints, `data: {"clarify": {"question", "options"}}` when the agent asks a
// follow-up question instead of answering, and a trailing `data: [DONE]`.
// Mirrors the parsing used for the player-insights stream in PlayerProfilePage.js.
async function streamAsk(resp, { onText, onStatus, onClarify }) {
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let lineBuffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    lineBuffer += decoder.decode(value, { stream: true });
    const lines = lineBuffer.split('\n');
    lineBuffer = lines.pop();
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const data = line.slice(6).trim();
      if (data === '[DONE]') continue;
      try {
        const obj = JSON.parse(data);
        if (obj.text) onText(obj.text);
        else if (obj.clarify) onClarify(obj.clarify);
        else if (obj.status) onStatus(obj.status, obj.tool);
        else if (obj.error) throw new Error(obj.error);
      } catch (e) {
        if (e instanceof SyntaxError) continue; // partial/non-JSON chunk, ignore
        throw e;
      }
    }
  }
}

// What the model sees of a clarifying question on the next turn: the
// question plus its options, so a reply like "card + roulette" has context.
function clarifyAsHistory({ question, options }) {
  return options.length ? `${question}\nOptions: ${options.join(' | ')}` : question;
}

function QuickReplies({ options, onPick }) {
  return (
    <div className="flex flex-wrap gap-2 pl-1">
      {options.map(o => (
        <button
          key={o}
          type="button"
          onClick={() => onPick(o)}
          className="px-3 py-1.5 text-xs rounded-md border border-cta/40 text-ink hover:bg-cta/15 cursor-pointer transition-colors"
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function ChatBubble({ role, content, scopeLabel }) {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
          isUser ? 'bg-cta/15 text-ink border border-cta/30' : 'bg-surface-2 text-ink border border-white/10'
        }`}
      >
        {isUser ? content : linkify(content)}
        {scopeLabel && <p className="mt-1.5 text-[11px] text-ink-dim">Scope: {scopeLabel}</p>}
      </div>
    </div>
  );
}

export default function GameAskAI() {
  const { activeBrand } = useBrand();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [scope, setScope] = useState(EMPTY_SCOPE);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [optionsError, setOptionsError] = useState('');
  const abortRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);
  useEffect(() => {
    let cancelled = false;
    // Nothing from the previous brand carries over: its producers/providers/games
    // (picked or pickable) don't apply, and its conversation must not be sent as
    // history for this one.
    abortRef.current?.abort();
    setScope(EMPTY_SCOPE);
    setOptions(EMPTY_OPTIONS);
    setMessages([]);
    setError('');
    setOptionsError('');
    gameDepartmentAPI.getAskOptions()
      .then(res => {
        if (!cancelled) setOptions({ producers: res.data?.producers || [], providers: res.data?.providers || [] });
      })
      .catch(() => { if (!cancelled) setOptionsError('Could not load producers/providers — those pickers are empty.'); });
    return () => { cancelled = true; };
  }, [activeBrand]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, streaming]);

  const ask = async (query) => {
    const question = query.trim();
    if (!question || streaming) return;

    setError('');
    setInput('');
    const history = messages.map(({ role, content, clarify }) => ({
      role,
      content: clarify ? clarifyAsHistory(clarify) : content,
    }));
    const requestScope = toRequestScope(scope);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: question, scopeLabel: describeScope(scope) },
      { role: 'assistant', content: '' },
    ]);
    setStreaming(true);
    setStatus('Thinking…');

    const controller = new AbortController();
    abortRef.current = controller;
    const headers = { 'Content-Type': 'application/json' };
    if (activeBrand) headers['X-Brand'] = activeBrand;

    try {
      const resp = await fetch('/api/game-department/ask', {
        method: 'POST',
        headers,
        body: JSON.stringify({ query: question, history, scope: requestScope }),
        signal: controller.signal,
        credentials: 'include',
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        const msg = body.error || `HTTP ${resp.status}`;
        throw new Error(msg === 'llm_key_not_configured' ? 'LLM API key not configured.' : msg);
      }
      await streamAsk(resp, {
        onText: (text) => {
          setStatus('');
          setMessages(prev => {
            const next = [...prev];
            next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + text };
            return next;
          });
        },
        onClarify: (clarify) => {
          setStatus('');
          setMessages(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            const content = last.content ? `${last.content}\n\n${clarify.question}` : clarify.question;
            next[next.length - 1] = { ...last, content, clarify: { ...clarify, options: clarify.options || [] } };
            return next;
          });
        },
        onStatus: (s, tool) => {
          if (s === 'tool_start') setStatus(TOOL_STATUS[tool] || 'Fetching game data…');
          else if (s === 'tool_done') setStatus('Analysing…');
        },
      });
    } catch (e) {
      if (e.name === 'AbortError') {
        setMessages(prev => prev.slice(0, -1));
      } else {
        setError(e.message || 'Something went wrong.');
        setMessages(prev => prev.slice(0, -1));
      }
    } finally {
      setStreaming(false);
      setStatus('');
    }
  };

  const handleSend = () => ask(input);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStop = () => abortRef.current?.abort();

  const handleClear = () => {
    if (streaming) return;
    setMessages([]);
    setError('');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-cta" />
              Ask AI
            </CardTitle>
            <CardDescription>
              Ask a free-text question about the game catalog — performance, retention, providers, or trends
              over time. Optionally pin exact games, producers, providers or dates in the scope below.
              Answers are grounded in live data from the reports on this page.
            </CardDescription>
          </div>
          {messages.length > 0 && (
            <Button variant="ghost" size="sm" onClick={handleClear} disabled={streaming}>
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Clear
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {error && <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        {messages.length === 0 ? (
          <div className="flex flex-col gap-2 mb-4">
            <p className="text-[11px] text-ink-dim">Try one of these, or ask your own question:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => ask(s)}
                  className="px-3 py-1.5 text-xs rounded-md border border-white/10 text-ink-dim hover:text-ink hover:border-cta/40 cursor-pointer transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 mb-4 max-h-[480px] overflow-y-auto pr-1">
            {messages.map((m, i) => (
              <React.Fragment key={i}>
                <ChatBubble
                  role={m.role}
                  scopeLabel={m.scopeLabel}
                  content={m.content || (streaming && i === messages.length - 1 ? '…' : '')}
                />
                {m.clarify?.options.length > 0 && i === messages.length - 1 && !streaming && (
                  <QuickReplies options={m.clarify.options} onPick={ask} />
                )}
              </React.Fragment>
            ))}
            {streaming && status && (
              <div className="flex items-center gap-2 text-[11px] text-ink-dim pl-1">
                <Spinner size="sm" />
                {status}
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        )}

        {optionsError && <p className="text-[11px] text-ink-dim mb-2">{optionsError}</p>}
        <AskScopeBar scope={scope} onChange={setScope} options={options} disabled={streaming} />

        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about game performance, retention, providers…"
            disabled={streaming}
            className="min-h-[44px] max-h-[160px]"
            rows={1}
          />
          {streaming ? (
            <Button variant="outline" onClick={handleStop}>
              <Square className="h-3.5 w-3.5 mr-1.5" />
              Stop
            </Button>
          ) : (
            <Button onClick={handleSend} disabled={!input.trim()}>
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Ask
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
